import { lazy, Suspense, useEffect } from 'react'

import { createFileRoute, redirect } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import * as Sentry from '@sentry/react'

import { AskAIButton } from '@/features/search/ai/components/ask-ai-button'
import { useSearchPageContext } from '@/features/search/ai/hooks/use-search-page-context'

import '@/features/search/ai/register'

import { AIRailLayout } from '@/features/ai/components/panel/ai-rail-layout'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { Section } from '@/features/app-shell/section'
import { collectorQueries } from '@/features/integrations/collectors/queries'
import { integrationQueries } from '@/features/integrations/queries'
import { SearchPageHeader } from '@/features/search/components/page-header'
import { SearchFilters } from '@/features/search/components/search-filters'
import {
	IntegrationSelectSkeleton,
	QueryInputSkeleton,
	SearchResultsSkeleton,
	SearchSidebarSkeleton,
	SearchSkeleton,
	TimeRangeSkeleton,
} from '@/features/search/components/skeletons'
import { searchLogsQueries } from '@/features/search/queries'
import {
	ensureSearchTimeRange,
	searchRemountDeps,
} from '@/features/search/route-options'
import { searchSchema } from '@/features/search/types'

import { ROLES } from '@/shared/auth/constants'
import {
	getCurrentRole,
	getCurrentSKU,
	getCurrentTenant,
} from '@/shared/auth/store'
import { hasActiveSKU } from '@/shared/auth/utils/sku'
import { hasRequiredRole } from '@/shared/auth/utils/user'
import { useContainerWidth } from '@/shared/layout/hooks/use-container-width'
import { redirectHome } from '@/shared/routing/utils/redirect'

import { ErrorBoundary } from '@/components/common/error-boundary'

const QueryInput = lazy(() => import('@/features/search/components/input'))
const TimeRange = lazy(() => import('@/features/search/components/time-range'))
const IntegrationSelect = lazy(
	() => import('@/features/search/components/integration-select'),
)
const SearchSidebar = lazy(() => import('@/features/search/components/sidebar'))
const SearchResults = lazy(() => import('@/features/search/components/results'))

export const Route = createFileRoute('/_dashboard/search/')({
	validateSearch: zodValidator(searchSchema),

	beforeLoad: ({ search, location }) => {
		const sku = getCurrentSKU()
		const role = getCurrentRole()

		// Redirect if the SKU is not search enabled
		if (sku && !sku.is_search_enabled) redirectHome()

		// Redirect if the user does not have the required role
		if (
			(role && !hasRequiredRole(role, ROLES.Analyst)) ||
			(sku && !hasActiveSKU(sku))
		)
			throw redirect({ to: '/unauthorized' })

		ensureSearchTimeRange({ search, location })
	},

	// Awaits the log sources and both definition catalogues the Log Source and
	// Event Type selects need,
	// so `pendingComponent` shows until the page can render its top row. Not
	// awaited, the page mounted at once and each section resolved on its own,
	// shifting the layout. `prefetchQuery` never throws: a failed fetch lets
	// the page render, and `IntegrationSelect`'s boundary reports it.
	//
	// The saved-search list is not awaited: it sits behind its own `Suspense`,
	// whose fallback has the trigger's size.
	loader: async ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant()

		if (!tenant) return

		void queryClient.prefetchQuery(searchLogsQueries.savedSearchList(tenant))

		await Promise.all([
			queryClient.prefetchQuery(integrationQueries.listedSources(tenant)),
			queryClient.prefetchQuery(integrationQueries.available(tenant)),
			queryClient.prefetchQuery(collectorQueries.logSourceOptions(tenant)),
		])
	},

	// A Tenant switch keeps this route matched; remount so no section keeps
	// the previous Tenant's state.
	remountDeps: searchRemountDeps,

	head: () => ({
		meta: [
			{
				title: 'Search | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: SearchSkeleton,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const { ref, width } = useContainerWidth()
	const pageContext = useSearchPageContext()
	const searchState = Route.useSearch()

	// Stamp a Sentry breadcrumb on every URL state change so error reports
	// from the route carry the user's active search context.
	useEffect(() => {
		Sentry.addBreadcrumb({
			category: 'search',
			message: 'navigate',
			level: 'info',
			data: {
				integrations_count: searchState.int?.length ?? 0,
				has_query: !!searchState.q?.rules && searchState.q.rules.length > 0,
				filters_count: searchState.ft?.length ?? 0,
				time_range_kind:
					searchState.tr && 'preset' in searchState.tr
						? 'preset'
						: searchState.tr
							? 'absolute'
							: 'none',
			},
		})
	}, [searchState])

	return (
		<AIRailLayout pageContext={pageContext}>
			<Section ref={ref}>
				<SearchPageHeader action={<AskAIButton />} />

				<div className="flex w-full flex-col items-start justify-start gap-4">
					<div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row">
						<ErrorBoundary feature="search" section="integration-select">
							<Suspense fallback={<IntegrationSelectSkeleton />}>
								<IntegrationSelect />
							</Suspense>
						</ErrorBoundary>

						<Suspense fallback={<TimeRangeSkeleton />}>
							<TimeRange />
						</Suspense>
					</div>

					<Suspense fallback={<QueryInputSkeleton />}>
						<QueryInput />
					</Suspense>

					{/* Eager: URL state only, and the page skeleton renders it too. */}
					<SearchFilters />

					<div className="flex w-full flex-row items-start justify-start gap-6">
						<ErrorBoundary feature="search" section="sidebar">
							<Suspense fallback={<SearchSidebarSkeleton />}>
								<SearchSidebar parentRef={ref} parentWidth={width} />
							</Suspense>
						</ErrorBoundary>

						<ErrorBoundary feature="search" section="results">
							<Suspense fallback={<SearchResultsSkeleton />}>
								<SearchResults />
							</Suspense>
						</ErrorBoundary>
					</div>
				</div>
			</Section>
		</AIRailLayout>
	)
}
